import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import { selectors as offerReduxSelectors } from '@makemydeal/dr-offer-redux';
import type { ApplyingDealTemplate } from '@makemydeal/dr-dash-types';
import { ToastWithBackdropLoader } from '../../common/DataLoadingToast';
import { FirstPencilCreatingOverlay } from '../../firstPencil/FirstPencilCreatingOverlay';
import { Box } from '@interstate/components/Box';
import { Action } from '@interstate/components/Action';
import type { ToastType } from '../../common/DataLoadingToast/Toast';

const ERROR_MESSAGE = 'Something went wrong, template is not applied. Please try again.';
const SUCCESS_TITLE = 'Template Successfully Applied';
const REVERT_TOAST_TIMEOUT_MS = 20000;
const SUCCESS_MESSAGE_PREFIX = 'The deal template ';
const SUCCESS_MESSAGE_SUFFIX = ' has been applied to this deal.';

export const DataLoadingToastForManuallyAppliedTemplate = () => {
    const {
        isApplying: isLoading,
        isApplied: isLoaded,
        isError: isError,
        name: templateName
    } = useSelector(dealTemplateSelectors.getManuallyApplyingTemplateState) as ApplyingDealTemplate;
    const isFirstPencilTemplateApplying = useSelector(dealTemplateSelectors.isFirstPencilTemplateApplying);
    const isFirstPencilOverlayShown = useSelector(firstPencilSelectors.isTemplateApplyOverlayShown);

    const isMissingDealXgRecord = useSelector(offerReduxSelectors.getIsDealXgRecordMissed);

    const dispatch = useDispatch();

    const [hasReverted, setHasReverted] = useState(false);

    useEffect(() => {
        if (isLoading) {
            setHasReverted(false);
        }
    }, [isLoading]);

    const handleRevertClick = () => {
        setHasReverted(true);
        dispatch(dealTemplateActionCreators.revertManuallyAppliedTemplate());
    };

    const [isToastVisible, setIsToastVisible] = useState(false);

    const getMessage = (type: ToastType) => {
        if (type === 'error') {
            return ERROR_MESSAGE;
        }
        return (
            <>
                <b>{SUCCESS_TITLE}:</b> {SUCCESS_MESSAGE_PREFIX}
                <b>{templateName}</b>
                {SUCCESS_MESSAGE_SUFFIX}
                {!isMissingDealXgRecord && (
                    <>
                        {' '}
                        If you are unhappy with this template, you can{' '}
                        <Action
                            size="sm"
                            disabled={hasReverted}
                            onClick={handleRevertClick}
                            data-testid="manual-template-applied-revert-link"
                        >
                            revert it back to its original state
                        </Action>
                        .
                    </>
                )}
            </>
        );
    };

    return (
        <Box mb={2} display={isToastVisible ? 'block' : 'none'}>
            <ToastWithBackdropLoader
                {...{ isLoading, isError, isLoaded, setIsToastVisible }}
                getMessage={getMessage}
                timer={REVERT_TOAST_TIMEOUT_MS}
                hideLoader={isFirstPencilOverlayShown}
                loader={isFirstPencilTemplateApplying ? <FirstPencilCreatingOverlay /> : undefined}
            />
        </Box>
    );
};
